import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { useSession } from '@/auth/Session';
import { useBowtie } from '@/dqs/useBowtie';
import {
  Box,
  Button,
  Modal,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@/theme/components';
import { DateUtil } from '@/utils/index';
import { useScreenSize } from '@/utils/useScreenSize';

interface CancellationReasonsModalProps {
  /** If true, the modal is open */
  isOpen: boolean;
  /** Function to close the modal, passes the reason to further direct the cancel flow */
  onClose: (reason?: string) => void;
}

export function CancellationReasonsModal({
  isOpen,
  onClose,
}: CancellationReasonsModalProps) {
  const {
    session: { account },
  } = useSession();

  const { isMobile } = useScreenSize();

  const queryClient = useQueryClient();

  // STANDARD CANCEL MESSAGE
  const [endDate, setEndDate] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    const dateUtil = new DateUtil();

    setEndDate(
      dateUtil.parseDateFromString(
        account?.account?.subscription_info?.normalize_date || '',
        'MM/dd/yyyy'
      ) || ''
    );
    setName(account?.account?.user_info?.first_name || '');
  }, [account]);

  // CANCEL SURVEY
  const BOW_TIE_PLAN_ID = 'CANCEL_BV_01';
  const { questions, recordAnswers } = useBowtie(BOW_TIE_PLAN_ID);

  const [currentReason, setCurrentReason] = useState<string | null>(null);
  const [questionText, setQuestionText] = useState('');
  const [answerChoices, setAnswerChoices] = useState<string[]>([]);
  const [customAttribute, setCustomAttribute] = useState('');
  const [screenId, setScreenId] = useState('');

  useEffect(() => {
    if (questions?.data?.question_set?.questions) {
      const question = questions.data.question_set.questions[0];
      setQuestionText(question.text || question.question_text);
      setScreenId(question.screen_id);
      setAnswerChoices(question.response.answer_set);
      setCustomAttribute(question.response.custom_attribute);
    }
  }, [questions]);

  // EVENTS
  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const { value } = event.target as HTMLInputElement;
    setCurrentReason(value);
  };

  function handleClose(currentReason?: string) {
    onClose(currentReason);
    queryClient.refetchQueries(['account']);
  }

  const handleSubmit = () => {
    if (!currentReason) return;

    recordAnswers.mutate({
      id: BOW_TIE_PLAN_ID,
      answers: [
        {
          screen_id: screenId,
          custom_attribute: customAttribute,
          response: currentReason,
        },
      ],
    });
    handleClose(currentReason);
  };

  return (
    <Modal
      open={isOpen}
      title={`We will miss you ${name}...`}
      hasCloseIcon
      sx={{
        textAlign: 'center',
        padding: 0,
      }}
      onClose={() => handleClose()}
    >
      <Text mb={2} data-cy="cancel-message">
        We have cancelled your subscription. You will be able to access your
        membership until{' '}
        <Text
          variant="h3"
          sx={(theme) => ({
            display: 'block',
            color: theme.palette.primary.main,
          })}
          mt={2}
        >
          {endDate}
        </Text>
      </Text>
      {questionText && (
        <>
          <Box
            p={2}
            sx={{ border: '1px solid', borderRadius: '4px' }}
            bgcolor="secondary.light"
          >
            <Text mb={2}>{questionText}</Text>
            <RadioGroup onChange={handleRadioChange}>
              {answerChoices.map((choice) => (
                <Stack
                  key={choice}
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                >
                  <Radio
                    value={choice}
                    data-cy={choice}
                    checked={currentReason === choice}
                  />
                  <Text textAlign="left">{choice}</Text>
                </Stack>
              ))}
            </RadioGroup>
          </Box>
          <Stack
            direction={isMobile ? 'column-reverse' : 'row'}
            justifyContent="space-between"
            mt={2}
            p={0}
          >
            <Button
              variant="outlined"
              color="success"
              onClick={() => handleClose()}
            >
              Skip Survey
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={currentReason === null}
              onClick={() => handleSubmit()}
              sx={{ marginBottom: isMobile ? 1 : 0 }}
            >
              Submit Survey
            </Button>
          </Stack>
        </>
      )}
    </Modal>
  );
}
